import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { searchAPI } from "../API/Seach";
import { tokens } from "../theme/tokens";
import {
  mapApiItemToRow,
  buildCompleteDetailsPayloadFromRow,
} from "../shared/bookingHistoryShared";
import BookingHistoryDataTable from "../Components/BookingHistoryDataTable";
import { generateAndSaveBookingVoucherPdf } from "../Utils/bookingVoucherPdf";

function tabProps(index) {
  return { id: `admin-bh-tab-${index}`, "aria-controls": `admin-bh-tabpanel-${index}` };
}

function navigateToBookingDetail(navigate, row) {
  const payload = buildCompleteDetailsPayloadFromRow(row);
  if (!payload) {
    toast.error("Missing booking reference for this row.");
    return;
  }
  const q = new URLSearchParams({
    bookingId: String(payload.bookingId),
    supplierBookingId: payload.supplierBookingId,
    clientId: String(payload.clientId),
    userId: String(payload.userId),
  });
  navigate(`/booking-detail?${q.toString()}`);
}

/**
 * Admin: client-wide bookings + per-user bookings (same grid as transaction history).
 */
export default function AdminBookingHistory({ clientUsers = [], clientUsersLoading = false }) {
  const navigate = useNavigate();
  const cidRaw = localStorage.getItem("clientId");
  const clientId = cidRaw ? parseInt(cidRaw, 10) : NaN;

  const [mainTab, setMainTab] = useState(0);

  const [cPage, setCPage] = useState(0);
  const [cRowsPerPage, setCRowsPerPage] = useState(10);
  const [cRows, setCRows] = useState([]);
  const [cTotal, setCTotal] = useState(0);
  const [cLoading, setCLoading] = useState(false);

  const [selUser, setSelUser] = useState("");
  const [uPage, setUPage] = useState(0);
  const [uRowsPerPage, setURowsPerPage] = useState(10);
  const [uRows, setURows] = useState([]);
  const [uTotal, setUTotal] = useState(0);
  const [uLoading, setULoading] = useState(false);

  /** @type {Record<string, { rows: any[]; total: number; page: number; rowsPerPage: number; loading: boolean; loaded: boolean }>} */
  const [accByUser, setAccByUser] = useState({});
  const accordionFetchedRef = useRef(new Set());
  const [downloadingBookingId, setDownloadingBookingId] = useState(null);

  const loadClientBookings = useCallback(async () => {
    if (!cidRaw || Number.isNaN(clientId)) {
      setCRows([]);
      setCTotal(0);
      return;
    }
    setCLoading(true);
    try {
      const res = await searchAPI.getBookingsByClient(clientId, cPage + 1, cRowsPerPage);
      const ok =
        res?.status === "success" && Number(res?.statusCode) === 200 && res.data;
      if (ok) {
        const items = res.data.items || [];
        setCRows(items.map(mapApiItemToRow));
        setCTotal(Number(res.data.totalCount) || items.length);
      } else {
        toast.error(res?.message || "Failed to load client bookings.");
        setCRows([]);
        setCTotal(0);
      }
    } catch {
      toast.error("Failed to load client bookings.");
      setCRows([]);
      setCTotal(0);
    } finally {
      setCLoading(false);
    }
  }, [cidRaw, clientId, cPage, cRowsPerPage]);

  const loadUserTabBookings = useCallback(async () => {
    const uid = parseInt(selUser, 10);
    if (!cidRaw || Number.isNaN(clientId) || !selUser || Number.isNaN(uid)) {
      setURows([]);
      setUTotal(0);
      return;
    }
    setULoading(true);
    try {
      const res = await searchAPI.getBookingsByClientUser(
        clientId,
        uid,
        uPage + 1,
        uRowsPerPage
      );
      const ok =
        res?.status === "success" && Number(res?.statusCode) === 200 && res.data;
      if (ok) {
        const items = res.data.items || [];
        setURows(items.map(mapApiItemToRow));
        setUTotal(Number(res.data.totalCount) || items.length);
      } else {
        toast.error(res?.message || "Failed to load user bookings.");
        setURows([]);
        setUTotal(0);
      }
    } catch {
      toast.error("Failed to load user bookings.");
      setURows([]);
      setUTotal(0);
    } finally {
      setULoading(false);
    }
  }, [cidRaw, clientId, selUser, uPage, uRowsPerPage]);

  useEffect(() => {
    if (mainTab !== 0) return;
    loadClientBookings();
  }, [mainTab, loadClientBookings]);

  useEffect(() => {
    if (mainTab !== 1) return;
    if (!selUser) {
      setURows([]);
      setUTotal(0);
      return;
    }
    loadUserTabBookings();
  }, [mainTab, selUser, loadUserTabBookings]);

  const loadAccordionUser = useCallback(
    async (userId, page, rowsPerPage) => {
      if (!cidRaw || Number.isNaN(clientId)) return;
      setAccByUser((prev) => ({
        ...prev,
        [userId]: { ...(prev[userId] || {}), loading: true },
      }));
      try {
        const res = await searchAPI.getBookingsByClientUser(
          clientId,
          userId,
          page + 1,
          rowsPerPage
        );
        const ok =
          res?.status === "success" && Number(res?.statusCode) === 200 && res.data;
        const items = ok ? res.data.items || [] : [];
        const total = ok ? Number(res.data.totalCount) || items.length : 0;
        setAccByUser((prev) => ({
          ...prev,
          [userId]: {
            rows: items.map(mapApiItemToRow),
            total,
            page,
            rowsPerPage,
            loading: false,
            loaded: true,
          },
        }));
      } catch {
        setAccByUser((prev) => ({
          ...prev,
          [userId]: {
            rows: [],
            total: 0,
            page: 0,
            rowsPerPage,
            loading: false,
            loaded: true,
          },
        }));
      }
    },
    [cidRaw, clientId]
  );

  const handleAccordion = (userId) => (_, expanded) => {
    if (!expanded) return;
    if (accordionFetchedRef.current.has(userId)) return;
    accordionFetchedRef.current.add(userId);
    loadAccordionUser(userId, 0, 5);
  };

  const isPastBooking = (row) => {
    const dateVal = row?.checkOut || row?.checkIn;
    if (!dateVal) return false;
    const bookingDate = new Date(dateVal);
    if (Number.isNaN(bookingDate.getTime())) return false;
    bookingDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return bookingDate < today;
  };

  const handleDownloadPdf = useCallback(async (row) => {
    const payload = buildCompleteDetailsPayloadFromRow(row);
    if (!payload) {
      toast.error("Missing booking reference for this row.");
      return;
    }
    setDownloadingBookingId(row.bookingId);
    try {
      const response = await searchAPI.getBookingCompleteDetails(payload);
      const ok =
        response?.status === "success" &&
        Number(response?.statusCode) === 200 &&
        response?.data;
      if (ok) {
        await generateAndSaveBookingVoucherPdf(response.data);
        toast.success("Booking voucher downloaded.");
      } else {
        toast.error(response?.message || "Could not load booking for PDF.");
      }
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to download PDF.";
      toast.error(msg);
    } finally {
      setDownloadingBookingId(null);
    }
  }, []);

  const handleCancel = async (row) => {
    const bookingId = row?.bookingId;
    if (bookingId == null || bookingId === "") {
      toast.error("Missing booking id.");
      return;
    }
    if (isPastBooking(row)) {
      toast.info("You cannot cancel past bookings.");
      return;
    }
    try {
      const response = await searchAPI.cancelHotelBookingById(bookingId);
      const ok =
        response?.status === "success" &&
        Number(response?.statusCode) === 200;
      if (ok) {
        toast.success("Booking cancelled successfully");
        accordionFetchedRef.current = new Set();
        setAccByUser({});
        await loadClientBookings();
        if (mainTab === 1 && selUser) await loadUserTabBookings();
      } else {
        toast.error(response?.message || "Failed to cancel booking");
      }
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to cancel booking";
      toast.error(msg);
    }
  };

  const invalidClient = !cidRaw || Number.isNaN(clientId);

  return (
    <Stack spacing={2.5}>
      <Stack direction="row" alignItems="flex-start" spacing={1.5}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: `${tokens.radiusMd}px`,
            bgcolor: tokens.accentSoft,
            color: tokens.accent,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <ReceiptLongOutlinedIcon />
        </Box>
        <Box>
          <Typography variant="h5" sx={{ fontFamily: tokens.fontDisplay, fontWeight: 700, color: tokens.brand }}>
            Booking history
          </Typography>
          <Typography variant="body2" sx={{ color: tokens.textMuted, mt: 0.5 }}>
            All bookings for your client, or filter by a single user (same data as user booking history).
          </Typography>
        </Box>
      </Stack>

      {invalidClient ? (
        <Typography variant="body2" sx={{ color: tokens.error }}>
          Client ID is missing. Sign in again to load bookings.
        </Typography>
      ) : (
        <Paper
          elevation={0}
          sx={{
            border: `1px solid ${tokens.border}`,
            borderRadius: `${tokens.radiusLg}px`,
            boxShadow: tokens.shadowSm,
            overflow: "hidden",
          }}
        >
          <Tabs
            value={mainTab}
            onChange={(_, v) => {
              setMainTab(v);
              setCPage(0);
              setUPage(0);
            }}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              px: 2,
              pt: 1.5,
              borderBottom: `1px solid ${tokens.border}`,
              "& .MuiTab-root": { textTransform: "none", fontWeight: 700 },
              "& .Mui-selected": { color: tokens.brand },
            }}
          >
            <Tab label="Client bookings" {...tabProps(0)} />
            <Tab label="User bookings" {...tabProps(1)} />
          </Tabs>

          <Box role="tabpanel" hidden={mainTab !== 0} id="admin-bh-tabpanel-0" sx={{ p: { xs: 2, sm: 2.5 } }}>
            {mainTab === 0 && (
              <Stack spacing={3}>
                <Typography variant="subtitle2" sx={{ color: tokens.textMuted, fontWeight: 600 }}>
                  Paginated list:{" "}
                  <Typography component="span" variant="body2" sx={{ color: tokens.text, fontWeight: 500 }}>
                    GET /api/Booking/client/{`{clientId}`}
                  </Typography>
                </Typography>
                <BookingHistoryDataTable
                  rows={cRows}
                  loading={cLoading}
                  page={cPage}
                  rowsPerPage={cRowsPerPage}
                  totalCount={cTotal}
                  onPageChange={(_, p) => setCPage(p)}
                  onRowsPerPageChange={(e) => {
                    setCRowsPerPage(+e.target.value);
                    setCPage(0);
                  }}
                  onView={(row) => navigateToBookingDetail(navigate, row)}
                  onCancel={handleCancel}
                  onDownload={handleDownloadPdf}
                  downloadingBookingId={downloadingBookingId}
                />

                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: tokens.brand, mb: 1.5 }}>
                    By user
                  </Typography>
                  <Typography variant="body2" sx={{ color: tokens.textMuted, mb: 2 }}>
                    Expand a team member to load their bookings only (
                    <Typography component="span" variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                      /api/Booking/client/{`{clientId}`}/user/{`{userId}`}
                    </Typography>
                    ).
                  </Typography>
                  {clientUsersLoading ? (
                    <Typography variant="body2" color="text.secondary">
                      Loading users…
                    </Typography>
                  ) : clientUsers.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No users found for this client.
                    </Typography>
                  ) : (
                    <Stack spacing={1}>
                      {clientUsers.map((u) => (
                        <Accordion
                          key={u.userId}
                          disableGutters
                          elevation={0}
                          onChange={handleAccordion(u.userId)}
                          sx={{
                            border: `1px solid ${tokens.border}`,
                            borderRadius: `${tokens.radiusMd}px !important`,
                            "&:before": { display: "none" },
                            overflow: "hidden",
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon sx={{ color: tokens.accent }} />}
                            sx={{
                              bgcolor: tokens.surfaceAlt,
                              minHeight: 52,
                              "& .MuiAccordionSummary-content": { my: 1 },
                            }}
                          >
                            <Stack spacing={0.25}>
                              <Typography fontWeight={700} sx={{ color: tokens.brand }}>
                                {u.userName || "—"}
                              </Typography>
                              <Typography variant="caption" sx={{ color: tokens.textMuted }}>
                                {u.email} · User ID {u.userId} · {u.role}
                              </Typography>
                            </Stack>
                          </AccordionSummary>
                          <AccordionDetails sx={{ p: 0, bgcolor: tokens.paper }}>
                            {(() => {
                              const st = accByUser[u.userId];
                              if (!st) {
                                return (
                                  <Box sx={{ p: 2 }}>
                                    <Typography variant="body2" color="text.secondary">
                                      Expand to load bookings.
                                    </Typography>
                                  </Box>
                                );
                              }
                              return (
                                <Box sx={{ p: 1.5 }}>
                                  <BookingHistoryDataTable
                                    rows={st.rows || []}
                                    loading={Boolean(st.loading)}
                                    page={st.page ?? 0}
                                    rowsPerPage={st.rowsPerPage ?? 5}
                                    totalCount={st.total ?? 0}
                                    onPageChange={(_, p) =>
                                      loadAccordionUser(u.userId, p, st.rowsPerPage ?? 5)
                                    }
                                    onRowsPerPageChange={(e) => {
                                      const rp = +e.target.value;
                                      loadAccordionUser(u.userId, 0, rp);
                                    }}
                                    onView={(row) => navigateToBookingDetail(navigate, row)}
                                    onCancel={handleCancel}
                                    onDownload={handleDownloadPdf}
                                    downloadingBookingId={downloadingBookingId}
                                    densePagination
                                    emptyMessage="No bookings for this user."
                                  />
                                </Box>
                              );
                            })()}
                          </AccordionDetails>
                        </Accordion>
                      ))}
                    </Stack>
                  )}
                </Box>
              </Stack>
            )}
          </Box>

          <Box role="tabpanel" hidden={mainTab !== 1} id="admin-bh-tabpanel-1" sx={{ p: { xs: 2, sm: 2.5 } }}>
            {mainTab === 1 && (
              <Stack spacing={2}>
                <FormControl fullWidth size="small" sx={{ maxWidth: 420 }}>
                  <InputLabel id="admin-bh-user-label">User</InputLabel>
                  <Select
                    labelId="admin-bh-user-label"
                    label="User"
                    value={selUser}
                    onChange={(e) => {
                      setSelUser(e.target.value);
                      setUPage(0);
                    }}
                  >
                    <MenuItem value="">
                      <em>Select a user</em>
                    </MenuItem>
                    {clientUsers.map((u) => (
                      <MenuItem key={u.userId} value={String(u.userId)}>
                        {u.userName} ({u.email})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {!selUser ? (
                  <Typography variant="body2" color="text.secondary">
                    Choose a user to load their bookings from the API.
                  </Typography>
                ) : (
                  <BookingHistoryDataTable
                    rows={uRows}
                    loading={uLoading}
                    page={uPage}
                    rowsPerPage={uRowsPerPage}
                    totalCount={uTotal}
                    onPageChange={(_, p) => setUPage(p)}
                    onRowsPerPageChange={(e) => {
                      setURowsPerPage(+e.target.value);
                      setUPage(0);
                    }}
                    onView={(row) => navigateToBookingDetail(navigate, row)}
                    onCancel={handleCancel}
                    onDownload={handleDownloadPdf}
                    downloadingBookingId={downloadingBookingId}
                  />
                )}
              </Stack>
            )}
          </Box>
        </Paper>
      )}
    </Stack>
  );
}
